import { AbsoluteHref } from '@weenat/client/dist/routx/runtime-core'
import { useIntl } from '@weenat/wintl'
import { Redirect, useNavigate } from 'app/routx-router'
import { IconComponent } from 'app/src/kit/Icon'
import Icons from 'app/src/kit/Icons'
import Text from 'app/src/kit/Text'
import TextTooltip from 'app/src/kit/tooltips/TextTooltip'
import { useFavorites, useToggleFeature } from 'app/state'
import { isNil } from 'lodash-es'
import { MouseEvent } from 'react'
import { styled } from 'styled-components'

const PageContainer = styled(Flex)`
  flex-direction: column;
  flex: 1;
  padding: ${(p) => p.theme.spacings.xl}px;
  overflow-y: auto;
  gap: ${(p) => p.theme.spacings.lg}px;
`

const FavoriteRow = styled(Flex)`
  align-items: center;
  gap: ${(p) => p.theme.spacings.md}px;
  padding: ${(p) => p.theme.spacings.md}px;
  border-radius: ${(p) => p.theme.radiuses.md}px;
  cursor: pointer;

  &:hover {
    background-color: ${(p) => p.theme.colors.grayscale[100]};
  }
`

function Favorites() {
  const [{ on_nav_favorites }] = useToggleFeature()
  const [favorites, setFavorites] = useFavorites()
  const { t } = useIntl()
  const nav = useNavigate()

  if (!on_nav_favorites) return <Redirect to={'/' as AbsoluteHref} />

  const removeFavorite = (path: string, e: MouseEvent) => {
    e.stopPropagation()
    setFavorites((prev) => prev.filter((f) => f.path !== path))
  }

  function extractLeadingEmoji(name: string): string | null {
    const match = name.match(/^\p{Emoji_Presentation}/u)
    return match ? match[0] : null
  }

  return (
    <PageContainer>
      <Text $fontWeight='bold' $fontSize='lg'>
        {t('titles.favorites')}
      </Text>

      {favorites.length === 0 ? (
        <Flex $alignItems='center' $justifyContent='center' $flex={1}>
          <Text $color='grayscale.700'>{t('favorites.empty_state')}</Text>
        </Flex>
      ) : (
        <Flex $flexDirection='column'>
          {favorites.map((fav) => {
            const emoji = extractLeadingEmoji(fav.name)
            const IconComp = (Icons as Record<string, IconComponent>)[fav.icon] ?? Icons.Star

            return (
              <FavoriteRow key={fav.path} onClick={() => nav(fav.path as AbsoluteHref)}>
                {!isNil(emoji) ? (
                  <Text $fontSize='xl'>{emoji}</Text>
                ) : (
                  <IconComp $size='lg' $color='grayscale.black' />
                )}
                <Box $flex={1}>
                  <Text>{!isNil(emoji) ? fav.name.slice(emoji.length).trimStart() : fav.name}</Text>
                </Box>
                <TextTooltip restMs={500} content={t('actions.remove_from_favorite')}>
                  <Icons.Close
                    $size='sm'
                    $p='sm'
                    $color='grayscale.700'
                    onPress={(e) => removeFavorite(fav.path, e)}
                  />
                </TextTooltip>
              </FavoriteRow>
            )
          })}
        </Flex>
      )}
    </PageContainer>
  )
}

export const Component = Favorites
export default Favorites

export const ErrorBoundary = () => null
